import { setAuthContext, setBrandContext } from './context';

import { useAuth } from '@/shared/hooks/useAuth';
import { useBrandStore } from '@/shared/stores/useBrandStore';

const resolveSelectedBrand = () => {
  const { user } = useAuth.getState();
  const { brandId } = useBrandStore.getState();
  if (!brandId || !user) return null;
  return user.brands.find((b) => b.id === brandId) ?? null;
};

const sync = (): void => {
  const { user, hasCheckedAuth } = useAuth.getState();

  // Leave the snapshot at 'unknown' until the session question is actually
  // answered. Reporting 'guest' before then would label an in-flight probe as
  // a confirmed anonymous visitor, which is the distinction these sentinels
  // exist to preserve.
  if (!user && !hasCheckedAuth) return;

  // setAuthContext clears the brand, so the brand is always reapplied after it.
  setAuthContext(user);
  setBrandContext(resolveSelectedBrand());
};

let stop: (() => void) | null = null;

/**
 * Mirror auth + brand state into the analytics snapshot.
 *
 * Driven by Zustand subscriptions rather than React effects on purpose: `set()`
 * invokes subscribers synchronously, so the snapshot is current before React
 * even schedules a re-render — and therefore before any navigation triggered by
 * that state change can produce a `$pageview`. An effect-based writer always
 * leaves a window in which events are captured against stale identity, which is
 * exactly the bug this replaces.
 *
 * Must never be imported by `posthog.ts`: that would close the cycle
 * posthog → contextSync → useAuth → posthog.
 */
export function startAnalyticsContextSync(): () => void {
  if (stop) return stop;

  sync();

  const unsubscribeAuth = useAuth.subscribe((state, prev) => {
    // hasCheckedAuth matters on its own: a probe that resolves to "no session",
    // or one skipped on a public route, leaves user === null === prev.user, and
    // without this edge the snapshot would sit at 'unknown' for the whole visit
    // instead of moving to 'guest'.
    if (
      state.user !== prev.user ||
      state.hasCheckedAuth !== prev.hasCheckedAuth
    ) {
      sync();
    }
  });

  const unsubscribeBrand = useBrandStore.subscribe((state, prev) => {
    if (state.brandId !== prev.brandId) sync();
  });

  stop = () => {
    unsubscribeAuth();
    unsubscribeBrand();
    stop = null;
  };

  return stop;
}
